import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ArrowRight, ArrowLeft } from 'lucide-react';

export default function StepByStep({ steps = [] }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  function goNext() {
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  }

  function goPrev() {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }

  function toggleStep(idx) {
    if (idx === currentStep) return;
    // Allow clicking completed or next step
    if (completedSteps.has(idx) || idx === currentStep + 1) {
      setCurrentStep(idx);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = completedSteps.has(idx);
        const isLast = idx === steps.length - 1;

        return (
          <div key={idx} className="flex gap-4">
            {/* Timeline column */}
            <div className="flex flex-col items-center shrink-0">
              {/* Circle */}
              <motion.button
                onClick={() => toggleStep(idx)}
                className={`relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                  isCompleted
                    ? 'bg-green-500 border-green-500 text-white cursor-pointer'
                    : isActive
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
                whileTap={isCompleted ? { scale: 0.95 } : {}}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="text-xs font-bold">{idx + 1}</span>
                )}
                {isActive && !isCompleted && (
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-blue-400"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                )}
              </motion.button>

              {/* Line */}
              {!isLast && (
                <div
                  className={`w-0.5 flex-1 min-h-[16px] transition-colors duration-200 ${
                    isCompleted ? 'bg-green-300' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>

            {/* Content column */}
            <div className={`pb-6 flex-1 min-w-0 ${isLast ? '' : ''}`}>
              <button
                onClick={() => toggleStep(idx)}
                className={`flex items-center gap-2 text-left w-full ${
                  isActive || isCompleted ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <h4
                  className={`text-sm font-semibold transition-colors duration-150 ${
                    isActive
                      ? 'text-gray-900'
                      : isCompleted
                      ? 'text-green-700'
                      : 'text-gray-400'
                  }`}
                >
                  {step.title}
                </h4>
                {!isActive && !isCompleted && (
                  <ChevronDown className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                )}
              </button>

              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-2 text-sm text-gray-700 leading-relaxed">
                      {step.content}
                    </div>

                    {step.highlight && (
                      <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                        {step.highlight}
                      </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="mt-4 flex items-center gap-3">
                      {idx > 0 && (
                        <button
                          onClick={goPrev}
                          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                          Previous Step
                        </button>
                      )}
                      {!isLast && (
                        <button
                          onClick={goNext}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors ml-auto"
                        >
                          Next Step
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isLast && !completedSteps.has(idx) && (
                        <button
                          onClick={() =>
                            setCompletedSteps((prev) => new Set([...prev, idx]))
                          }
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 transition-colors ml-auto"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        );
      })}
    </div>
  );
}
